import {
  Calendar,
  ChevronLeft,
  Copy,
  DollarSign,
  Download,
  Edit,
  FileText,
  Mail,
  MapPin,
  Package,
  RefreshCw,
  Truck,
  User,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
// Icons
import awbService, { AWBDetail } from '@/services/awbService';
import { useNavigate, useParams } from 'react-router-dom';

import AWBDocumentsTab from '@/components/awb/AWBDocumentsTab';
// Services
import AWBKYCTab from '@/components/awb/AWBKYCTab';
// UI Components
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useToast } from '@/hooks/useToast';
import { getApiClient } from '@/services/apiClient';

// ============================================================================
// TYPES
// ============================================================================

interface MasterDataOption {
  _id: string;
  name: string;
  code: string;
  symbol?: string;
}

// ============================================================================
// STATUS BADGE
// ============================================================================

const getStatusColor = (status: string) => {
  const colors: { [key: string]: string } = {
    DRAFT: 'bg-gray-100 text-gray-800 border-gray-300',
    BOOKED: 'bg-blue-100 text-blue-800 border-blue-300',
    PICKED_UP: 'bg-purple-100 text-purple-800 border-purple-300',
    IN_TRANSIT: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    OUT_FOR_DELIVERY: 'bg-orange-100 text-orange-800 border-orange-300',
    DELIVERED: 'bg-green-100 text-green-800 border-green-300',
    RETURNED: 'bg-red-100 text-red-800 border-red-300',
    CANCELLED: 'bg-gray-200 text-gray-600 border-gray-400',
  };
  return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const ReadOnlyField: React.FC<{
  label: string;
  value: string | number | undefined | null;
  className?: string;
  highlight?: boolean;
}> = ({ label, value, className = '', highlight = false }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className='text-xs font-medium text-gray-500 dark:text-gray-400'>
      {label}
    </Label>
    <div
      className={`text-sm font-medium px-2 py-1.5 rounded border ${
        highlight
          ? 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200'
          : 'bg-gray-50 border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100'
      }`}
    >
      {value === null || value === undefined || value === '' ? '-' : value}
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AWBViewPageNew: React.FC = () => {
  const { awbNumber } = useParams<{ awbNumber: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const api = useMemo(() => getApiClient({ useWindowInstance: true }), []);

  const [awbData, setAwbData] = useState<AWBDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    | 'awb'
    | 'sales'
    | 'purchase'
    | 'tracking'
    | 'kyc'
    | 'audit'
    | 'documents'
    | 'extra'
  >('awb');
  const [isTrackingExpanded, setIsTrackingExpanded] = useState(false);
  const [expandedAuditLogs, setExpandedAuditLogs] = useState<Set<number>>(
    new Set()
  );

  // Master data for display names
  const [masterData, setMasterData] = useState<{
    customers: MasterDataOption[];
    companies: MasterDataOption[];
    countries: MasterDataOption[];
    products: MasterDataOption[];
    services: MasterDataOption[];
    currencies: MasterDataOption[];
    vendors: MasterDataOption[];
    hubs: MasterDataOption[];
  }>({
    customers: [],
    companies: [],
    countries: [],
    products: [],
    services: [],
    currencies: [],
    vendors: [],
    hubs: [],
  });

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    if (awbNumber) {
      loadAWBData();
      loadMasterData();
    } else {
      navigate('/awb', { replace: true });
    }
  }, [awbNumber]);

  const loadAWBData = async () => {
    if (!awbNumber) return;

    setIsLoading(true);
    try {
      const data = await awbService.getAWB(awbNumber);
      setAwbData(data);
    } catch (error) {
      console.error('Error loading AWB:', error);
      toast({
        title: 'Error',
        description: 'Failed to load AWB details',
        variant: 'destructive',
      });
      navigate('/awb', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const loadMasterData = async () => {
    try {
      const [
        customers,
        companies,
        countries,
        products,
        services,
        currencies,
        vendors,
        hubs,
      ] = await Promise.all([
        api.get('/api/master-data/customer').then(r => r.data.data?.data || []),
        api.get('/api/master-data/company').then(r => r.data.data?.data || []),
        api.get('/api/master-data/country').then(r => r.data.data?.data || []),
        api.get('/api/master-data/product').then(r => r.data.data?.data || []),
        api.get('/api/master-data/service').then(r => r.data.data?.data || []),
        api.get('/api/master-data/currency').then(r => r.data.data?.data || []),
        api.get('/api/master-data/vendor').then(r => r.data.data?.data || []),
        api
          .get('/api/master-data/hub')
          .catch(() => ({ data: { data: { data: [] } } }))
          .then(r => r.data.data?.data || []),
      ]);

      setMasterData({
        customers,
        companies,
        countries,
        products,
        services,
        currencies,
        vendors,
        hubs,
      });
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const masterNameLookup = useMemo(() => {
    const buildLookup = (items: MasterDataOption[]) => {
      const lookup = new Map<string, string>();
      items.forEach(item => {
        if (item?._id) {
          lookup.set(item._id, item.name || item.code || item._id);
        }
      });
      return lookup;
    };

    return {
      customers: buildLookup(masterData.customers),
      companies: buildLookup(masterData.companies),
      countries: buildLookup(masterData.countries),
      products: buildLookup(masterData.products),
      services: buildLookup(masterData.services),
      currencies: buildLookup(masterData.currencies),
      vendors: buildLookup(masterData.vendors),
      hubs: buildLookup(masterData.hubs),
    };
  }, [masterData]);

  const getMasterName = (
    lookup: Map<string, string>,
    value: MasterDataOption | string | undefined | null
  ): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') {
      if (value.name) return value.name;
      if (value.code) return value.code;
      if (value._id && lookup.has(value._id)) {
        return lookup.get(value._id) as string;
      }
      return value._id || '-';
    }

    const key = String(value);
    return lookup.get(key) || key;
  };

  const formatDate = (date: string | undefined): string => {
    if (!date) return '-';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCurrencyCode = (currency: any): string => {
    if (!currency) return 'INR';
    if (typeof currency === 'string') return 'INR';
    if (typeof currency === 'object' && currency !== null) {
      return currency.code || 'INR';
    }
    return 'INR';
  };

  const getCurrencySymbol = (currency: any): string => {
    if (!currency) return '₹';
    if (typeof currency === 'string') return '₹';
    if (typeof currency === 'object' && currency !== null) {
      return currency.symbol || '₹';
    }
    return '₹';
  };

  const formatCurrency = (
    amount: number | undefined,
    currencyOverride?: string
  ): string => {
    if (amount === undefined || amount === null) return '-';
    // Use the AWB's currency if available, otherwise use the override or default to INR
    const currencyCode = currencyOverride || getCurrencyCode(awbData?.currency);
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch {
      // Fallback if currency code is invalid
      return `${getCurrencySymbol(awbData?.currency)} ${amount.toFixed(2)}`;
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied',
        description: 'AWB number copied to clipboard',
      });
    } catch (error) {
      console.error('Failed to copy AWB number:', error);
      toast({
        title: 'Copy failed',
        description: 'Unable to copy the AWB number. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // ============================================================================
  // RENDER: LOADING
  // ============================================================================

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (!awbData) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center space-y-4'>
          <h2 className='text-2xl font-bold'>AWB Not Found</h2>
          <p className='text-gray-500'>The requested AWB could not be found.</p>
          <Button onClick={() => navigate('/awb')} variant='outline'>
            <ChevronLeft className='h-4 w-4 mr-2' />
            Back to AWB List
          </Button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: TABS
  // ============================================================================

  const renderTabs = () => (
    <div className='sticky top-0 z-20 flex overflow-x-auto border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur supports-[backdrop-filter]:bg-white/80'>
      {[
        { id: 'awb', label: 'AWB Details', shortLabel: 'AWB' },
        { id: 'kyc', label: 'KYC', shortLabel: 'KYC' },
        { id: 'sales', label: 'Sales Billing', shortLabel: '₹ SALES' },
        { id: 'purchase', label: 'Purchase Billing', shortLabel: '₹ PURCHASE' },
        { id: 'tracking', label: 'Tracking', shortLabel: 'TRACKING' },
        { id: 'documents', label: 'Documents', shortLabel: 'DOCS' },
        { id: 'audit', label: 'Audit Logs', shortLabel: 'LOGS' },
        // { id: 'extra', label: 'Extra', shortLabel: 'EXTRA' }, // Commented out for now
      ].map(tab => (
        <button
          key={tab.id}
          type='button'
          onClick={() => setActiveTab(tab.id as any)}
          className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
            activeTab === tab.id
              ? 'border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span className='hidden sm:inline'>{tab.label}</span>
          <span className='sm:hidden'>{tab.shortLabel}</span>
        </button>
      ))}
    </div>
  );

  // ============================================================================
  // RENDER: AWB DETAILS TAB
  // ============================================================================

  const renderAWBDetailsTab = () => (
    <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 gap-2'>
        <div className='flex items-center gap-3'>
          <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
            AWB DETAILS
          </h2>
          <Badge className={`${getStatusColor(awbData.currentStatus)} border`}>
            {awbData.currentStatus}
          </Badge>
        </div>
        <div className='flex items-center gap-2 flex-wrap'>
          <Button
            type='button'
            size='sm'
            variant='outline'
            onClick={() => copyToClipboard(awbData.awbNumber)}
            className='text-xs'
          >
            <Copy className='h-3 w-3 mr-1' />
            Copy AWB
          </Button>
          <Button
            type='button'
            size='sm'
            variant='outline'
            onClick={() => navigate(`/awb/${awbNumber}/edit`)}
            className='text-xs'
          >
            <Edit className='h-3 w-3 mr-1' />
            Edit
          </Button>
        </div>
      </div>

      {/* Content Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-12 gap-0'>
        {/* LEFT COLUMN: Basic Info */}
        <div className='lg:col-span-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4 space-y-4'>
          {/* AWB Number */}
          <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3'>
            <Label className='text-xs font-medium text-blue-600 dark:text-blue-400'>
              AWB NUMBER
            </Label>
            <div className='text-xl font-bold text-blue-900 dark:text-blue-100 mt-1'>
              {awbData.awbNumber}
            </div>
          </div>

          {/* Basic Fields */}
          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='CUSTOMER'
              value={getMasterName(masterNameLookup.customers, awbData.customer)}
            />
            <ReadOnlyField
              label='COMPANY'
              value={getMasterName(masterNameLookup.companies, awbData.company)}
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='BOOKING DATE'
              value={formatDate(awbData.bookingDate)}
            />
            <ReadOnlyField label='BOOKING TIME' value={awbData.bookingTime} />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='ORIGIN'
              value={getMasterName(masterNameLookup.countries, awbData.origin)}
            />
            <ReadOnlyField
              label='DESTINATION'
              value={getMasterName(masterNameLookup.countries, awbData.destination)}
              highlight
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='PRODUCT'
              value={getMasterName(masterNameLookup.products, awbData.product)}
            />
            <ReadOnlyField
              label='SERVICE'
              value={getMasterName(masterNameLookup.services, awbData.service)}
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='VENDOR'
              value={getMasterName(masterNameLookup.vendors, awbData.vendor)}
            />
            <ReadOnlyField
              label='ORIGIN HUB'
              value={getMasterName(masterNameLookup.hubs, awbData.originHub)}
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='FORWARDING NO.'
              value={awbData.forwardingNumber}
            />
            <ReadOnlyField
              label='FORWARDING NO. 2'
              value={awbData.forwardingNumber2}
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='REFERENCE NO.'
              value={awbData.referenceNumber}
            />
            <ReadOnlyField
              label='REFERENCE NAME'
              value={awbData.referenceName}
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='SHIPMENT VALUE'
              value={formatCurrency(awbData.shipmentValue)}
              highlight
            />
            <ReadOnlyField
              label='CURRENCY'
              value={getMasterName(masterNameLookup.currencies, awbData.currency)}
            />
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <ReadOnlyField
              label='PAYMENT METHOD'
              value={awbData.paymentMethod}
            />
            <ReadOnlyField label='INVOICE NO.' value={awbData.invoiceNumber} />
          </div>

          <ReadOnlyField label='REMARKS' value={awbData.remarks} />
        </div>

        {/* MIDDLE COLUMN: Shipper & Consignee */}
        <div className='lg:col-span-5 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Shipper */}
            <div className='space-y-3'>
              <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 border-b pb-2'>
                <User className='h-4 w-4 text-green-600' />
                SHIPPER
              </div>
              <ReadOnlyField label='NAME' value={awbData.shipper?.personName} />
              <ReadOnlyField label='COMPANY' value={awbData.shipper?.company} />
              <ReadOnlyField
                label='ADDRESS'
                value={[
                  awbData.shipper?.address1,
                  awbData.shipper?.address2,
                  awbData.shipper?.address3,
                ]
                  .filter(Boolean)
                  .join(', ')}
              />
              <div className='grid grid-cols-2 gap-2'>
                <ReadOnlyField label='CITY' value={awbData.shipper?.city} />
                <ReadOnlyField
                  label='STATE'
                  value={awbData.shipper?.stateCounty}
                />
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <ReadOnlyField
                  label='COUNTRY'
                  value={getMasterName(
                    masterNameLookup.countries,
                    awbData.shipper?.country
                  )}
                />
                <ReadOnlyField
                  label='ZIP'
                  value={awbData.shipper?.postZipCode}
                />
              </div>
              <ReadOnlyField
                label='PHONE'
                value={awbData.shipper?.phoneNumber}
              />
              <ReadOnlyField
                label='EMAIL'
                value={awbData.shipper?.emailAddress}
              />
              <div className='grid grid-cols-2 gap-2'>
                <ReadOnlyField
                  label='KYC TYPE'
                  value={awbData.shipper?.kycType}
                />
                <ReadOnlyField
                  label='KYC NO.'
                  value={awbData.shipper?.kycNumber}
                />
              </div>
            </div>

            {/* Consignee */}
            <div className='space-y-3'>
              <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 border-b pb-2'>
                <MapPin className='h-4 w-4 text-red-600' />
                CONSIGNEE
              </div>
              <ReadOnlyField
                label='NAME'
                value={awbData.consignee?.personName}
              />
              <ReadOnlyField
                label='COMPANY'
                value={awbData.consignee?.company}
              />
              <ReadOnlyField
                label='ADDRESS'
                value={[
                  awbData.consignee?.address1,
                  awbData.consignee?.address2,
                  awbData.consignee?.address3,
                ]
                  .filter(Boolean)
                  .join(', ')}
              />
              <div className='grid grid-cols-2 gap-2'>
                <ReadOnlyField label='CITY' value={awbData.consignee?.city} />
                <ReadOnlyField
                  label='STATE'
                  value={awbData.consignee?.stateCounty}
                />
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <ReadOnlyField
                  label='COUNTRY'
                  value={getMasterName(
                    masterNameLookup.countries,
                    awbData.consignee?.country
                  )}
                />
                <ReadOnlyField
                  label='ZIP'
                  value={awbData.consignee?.postZipCode}
                />
              </div>
              <ReadOnlyField
                label='PHONE'
                value={awbData.consignee?.phoneNumber}
              />
              <ReadOnlyField
                label='EMAIL'
                value={awbData.consignee?.emailAddress}
              />
              <div className='grid grid-cols-2 gap-2'>
                <ReadOnlyField
                  label='KYC TYPE'
                  value={awbData.consignee?.kycType}
                />
                <ReadOnlyField
                  label='KYC NO.'
                  value={awbData.consignee?.kycNumber}
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Weight Summary */}
        <div className='lg:col-span-3 p-4 space-y-4'>
          <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 border-b pb-2'>
            <Package className='h-4 w-4 text-blue-600' />
            WEIGHT SUMMARY
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-center'>
              <div className='text-xs text-blue-600 dark:text-blue-400 font-medium'>
                PCS
              </div>
              <div className='text-2xl font-bold text-blue-900 dark:text-blue-100'>
                {awbData.totalPieces || 0}
              </div>
            </div>
            <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center'>
              <div className='text-xs text-green-600 dark:text-green-400 font-medium'>
                Actual Weight
              </div>
              <div className='text-2xl font-bold text-green-900 dark:text-green-100'>
                {awbData.totalActualWeight?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div className='bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-center'>
              <div className='text-xs text-purple-600 dark:text-purple-400 font-medium'>
                Volumetric Weight
              </div>
              <div className='text-2xl font-bold text-purple-900 dark:text-purple-100'>
                {awbData.totalVolumetricWeight?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className='bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-center'>
              <div className='text-xs text-orange-600 dark:text-orange-400 font-medium'>
                Chargeable Weight
              </div>
              <div className='text-2xl font-bold text-orange-900 dark:text-orange-100'>
                {awbData.totalChargeableWeight?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>

          {/* Consigner and Add Weight */}
          {/* Consigner and Add Weight */}
          {((awbData.consignorWeight || 0) > 0 ||
            (awbData.addWeight || 0) > 0) && (
            <div className='grid grid-cols-2 gap-3'>
              {(awbData.consignorWeight || 0) > 0 && (
                <ReadOnlyField
                  label='Consigner Weight'
                  value={awbData.consignorWeight}
                />
              )}
              {(awbData.addWeight || 0) > 0 && (
                <ReadOnlyField label='ADD Weight' value={awbData.addWeight} />
              )}
            </div>
          )}

          {/* Weight Dimensions Table */}
          {awbData.weightDimensions && awbData.weightDimensions.length > 0 && (
            <div className='mt-4'>
              <div className='text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2'>
                PACKAGE DETAILS
              </div>
              {/* Desktop Table View */}
              <div className='hidden md:block overflow-x-auto'>
                <table className='w-full text-xs'>
                  <thead>
                    <tr className='bg-gray-100 dark:bg-gray-700'>
                      <th className='px-2 py-1 text-left whitespace-nowrap'>
                        Box No.
                      </th>
                      <th className='px-2 py-1 text-right whitespace-nowrap'>
                        Actual Weight
                      </th>
                      <th className='px-2 py-1 text-right whitespace-nowrap'>
                        L × B × H (cm)
                      </th>
                      <th className='px-2 py-1 text-right whitespace-nowrap'>
                        Volumetric Weight
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {awbData.weightDimensions.map((wd, idx) => (
                      <tr
                        key={idx}
                        className='border-b border-gray-100 dark:border-gray-700'
                      >
                        <td className='px-2 py-1'>{wd.boxNo}</td>
                        <td className='px-2 py-1 text-right'>
                          {wd.actualWeight}
                        </td>
                        <td className='px-2 py-1 text-right'>
                          {wd.length}×{wd.breadth}×{wd.height}
                        </td>
                        <td className='px-2 py-1 text-right'>
                          {wd.volumetricWeight?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className='md:hidden space-y-3'>
                {awbData.weightDimensions.map((wd, idx) => (
                  <div
                    key={idx}
                    className='bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-3 shadow-sm'
                  >
                    <div className='flex justify-between items-center mb-2 border-b border-gray-50 dark:border-gray-700 pb-2'>
                      <span className='text-xs font-semibold text-gray-500'>
                        Package #{wd.boxNo}
                      </span>
                    </div>

                    <div className='grid grid-cols-2 gap-2 text-xs'>
                      <div>
                        <div className='text-gray-500 text-[10px] uppercase tracking-wider'>
                          Actual Wt
                        </div>
                        <div className='font-medium'>{wd.actualWeight} kg</div>
                      </div>
                      <div>
                        <div className='text-gray-500 text-[10px] uppercase tracking-wider'>
                          Dimensions (cm)
                        </div>
                        <div className='font-medium'>
                          {wd.length}×{wd.breadth}×{wd.height}
                        </div>
                      </div>
                      <div className='col-span-2 mt-1 pt-1 border-t border-dashed border-gray-100 dark:border-gray-700'>
                        <div className='flex justify-between items-center'>
                          <span className='text-gray-500 text-[10px] uppercase tracking-wider'>
                            Volumetric Wt
                          </span>
                          <span className='font-medium'>
                            {wd.volumetricWeight?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shipment Invoice Section */}
      {awbData.shipmentInvoice &&
        awbData.shipmentInvoice.lineItems?.length > 0 && (
          <div className='border-t border-gray-200 dark:border-gray-700 p-4'>
            <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
              <FileText className='h-4 w-4 text-indigo-600' />
              SHIPMENT INVOICE
            </div>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-4'>
              <ReadOnlyField
                label='INVOICE TYPE'
                value={awbData.shipmentInvoice.invoiceType}
              />
              <ReadOnlyField
                label='CURRENCY'
                value={getMasterName(
                  masterNameLookup.currencies,
                  awbData.shipmentInvoice.currency
                )}
              />
              <ReadOnlyField
                label='INCOTERMS'
                value={awbData.shipmentInvoice.incoterms}
              />
              <ReadOnlyField
                label='TOTAL AMOUNT'
                value={formatCurrency(awbData.shipmentInvoice.totalAmount)}
                highlight
              />
            </div>
            {(awbData.shipmentInvoice.note ||
              awbData.shipmentInvoice.remarks) && (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                {awbData.shipmentInvoice.note && (
                  <ReadOnlyField
                    label='NOTE'
                    value={
                      awbData.shipmentInvoice.note === 'GIFT'
                        ? 'Gift'
                        : awbData.shipmentInvoice.note === 'SAMPLE'
                          ? 'Sample'
                          : awbData.shipmentInvoice.note
                    }
                  />
                )}
                {awbData.shipmentInvoice.remarks && (
                  <ReadOnlyField
                    label='REMARKS'
                    value={awbData.shipmentInvoice.remarks}
                  />
                )}
              </div>
            )}
            <div className='overflow-x-auto'>
              <table className='w-full text-xs'>
                <thead>
                  <tr className='bg-gray-100 dark:bg-gray-700'>
                    <th className='px-2 py-2 text-left'>Box No.</th>
                    <th className='px-2 py-2 text-left'>Sr. No.</th>
                    <th className='px-2 py-2 text-left'>Description</th>
                    <th className='px-2 py-2 text-left'>HS Code</th>
                    <th className='px-2 py-2 text-left'>Unit Type</th>
                    <th className='px-2 py-2 text-right'>Quantity</th>
                    <th className='px-2 py-2 text-right'>Unit Weight</th>
                    <th className='px-2 py-2 text-right'>IGST</th>
                    <th className='px-2 py-2 text-right'>Unit Rates</th>
                    <th className='px-2 py-2 text-right'>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {awbData.shipmentInvoice.lineItems.map((item, idx) => (
                    <tr
                      key={idx}
                      className='border-b border-gray-100 dark:border-gray-700'
                    >
                      <td className='px-2 py-2'>{item.boxNo || '-'}</td>
                      <td className='px-2 py-2'>{item.srNo}</td>
                      <td className='px-2 py-2'>{item.description}</td>
                      <td className='px-2 py-2'>{item.hsCode || '-'}</td>
                      <td className='px-2 py-2'>{item.unitType || '-'}</td>
                      <td className='px-2 py-2 text-right'>{item.quantity}</td>
                      <td className='px-2 py-2 text-right'>
                        {item.unitWeight || '-'}
                      </td>
                      <td className='px-2 py-2 text-right'>
                        {item.igst ? `${item.igst}%` : '-'}
                      </td>
                      <td className='px-2 py-2 text-right'>{item.unitRate}</td>
                      <td className='px-2 py-2 text-right font-medium'>
                        {item.amount?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );

  // ============================================================================
  // RENDER: SALES BILLING TAB
  // ============================================================================

  const renderSalesBillingTab = () => {
    const sales = (awbData as any).salesBilling;
    if (!sales) {
      return (
        <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 text-center'>
          <DollarSign className='h-12 w-12 text-gray-300 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-gray-600 dark:text-gray-400'>
            No Sales Billing Data
          </h3>
          <p className='text-sm text-gray-400 mt-2'>
            Sales billing information will appear here once added.
          </p>
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate(`/awb/${awbNumber}/edit`)}
            className='mt-4 text-xs'
          >
            <Edit className='h-3 w-3 mr-1' />
            Add Sales Billing
          </Button>
        </div>
      );
    }

    return (
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
        {/* Header */}
        <div className='flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
            AWB SALES BILLING
          </h2>
          <div className='flex items-center gap-2'>
            <Badge className='bg-green-100 text-green-800 border border-green-300 text-lg px-3 py-1'>
              {formatCurrency(sales.grandTotal || sales.finalAmount || 0)}
            </Badge>
            <Button
              variant='outline'
              size='sm'
              onClick={() => navigate(`/awb/${awbNumber}/edit`)}
              className='text-xs'
            >
              <Edit className='h-3 w-3 mr-1' />
              Edit
            </Button>
          </div>
        </div>

        {/* Main Content - 3 Column Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-0'>
          {/* LEFT COLUMN: AWB Information Summary */}
          <div className='lg:col-span-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4 space-y-3'>
            <h3 className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b pb-2'>
              Air Waybill Information
            </h3>

            <ReadOnlyField label='AWB NUMBER' value={awbData.awbNumber} />

            <div className='grid grid-cols-2 gap-2'>
              <ReadOnlyField
                label='CUSTOMER'
                value={getMasterName(masterNameLookup.customers, awbData.customer)}
              />
              <ReadOnlyField
                label='CODE'
                value={
                  masterData.customers.find(
                    c =>
                      c._id ===
                      (typeof awbData.customer === 'object'
                        ? (awbData.customer as any)?._id
                        : awbData.customer)
                  )?.code || '-'
                }
              />
            </div>

            <ReadOnlyField
              label='COMPANY'
              value={getMasterName(masterNameLookup.companies, awbData.company)}
            />

            <div className='grid grid-cols-2 gap-2'>
              <ReadOnlyField
                label='ORIGIN'
                value={getMasterName(masterNameLookup.countries, awbData.origin)}
              />
              <ReadOnlyField
                label='DESTINATION'
                value={getMasterName(masterNameLookup.countries, awbData.destination)}
              />
            </div>

            <ReadOnlyField
              label='PRODUCT'
              value={getMasterName(masterNameLookup.products, awbData.product)}
            />
            <ReadOnlyField
              label='SERVICE'
              value={getMasterName(masterNameLookup.services, awbData.service)}
            />
            <ReadOnlyField
              label='VENDOR'
              value={getMasterName(masterNameLookup.vendors, awbData.vendor)}
            />

            <div className='grid grid-cols-2 gap-2'>
              <ReadOnlyField
                label='BOOKING DATE'
                value={formatDate(awbData.bookingDate)}
              />
              <ReadOnlyField
                label='BOOKING TIME'
                value={awbData.bookingTime || '-'}
              />
            </div>

            <div className='grid grid-cols-2 gap-2'>
              <ReadOnlyField
                label='SHIPMENT VALUE'
                value={formatCurrency(awbData.shipmentValue || 0)}
              />
              <ReadOnlyField
                label='CURRENCY'
                value={getMasterName(masterNameLookup.currencies, awbData.currency)}
              />
            </div>

            <ReadOnlyField label='REMARKS' value={awbData.remarks || '-'} />
          </div>

          {/* MIDDLE COLUMN: Sales Billing Details */}
          <div className='lg:col-span-5 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4'>
            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
                SALES BILLING
              </h3>

              <div className='grid grid-cols-2 gap-3 mb-4'>
                <ReadOnlyField
                  label='SALES CURRENCY'
                  value={getMasterName(
                    masterNameLookup.currencies,
                    sales.salesCurrency || sales.currency
                  )}
                />
                <ReadOnlyField
                  label='GST TYPE'
                  value={sales.gstType || 'IGST'}
                />
              </div>

              <div className='flex items-center gap-4 mb-4 text-xs'>
                <span
                  className={`px-2 py-1 rounded ${sales.gstApplicable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  GST {sales.gstApplicable ? 'APPLICABLE' : 'NOT APPLICABLE'}
                </span>
              </div>

              <div className='space-y-2'>
                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='FREIGHT'
                    value={formatCurrency(sales.freight || 0)}
                  />
                  <ReadOnlyField
                    label='FREIGHT PER KG'
                    value={formatCurrency(sales.freightPerKg || 0)}
                  />
                </div>
                <ReadOnlyField
                  label='SEARCH CHARGE'
                  value={sales.searchCharge || '-'}
                />

                {/* Surcharges */}
                <div className='border-t pt-3 mt-3'>
                  <div className='text-xs font-semibold text-gray-600 mb-2'>
                    SURCHARGES
                  </div>
                  <div className='grid grid-cols-2 gap-2 text-xs'>
                    {sales.addressCorrection?.enabled && (
                      <ReadOnlyField
                        label='Address Correction'
                        value={`${formatCurrency(sales.addressCorrection.amount || 0)}${sales.addressCorrection.quantity > 0 ? ` (Qty: ${sales.addressCorrection.quantity})` : ''}`}
                      />
                    )}
                    {sales.clearanceCharge?.enabled && (
                      <ReadOnlyField
                        label='Clearance Charge'
                        value={`${formatCurrency(sales.clearanceCharge.amount || 0)}${sales.clearanceCharge.quantity > 0 ? ` (Qty: ${sales.clearanceCharge.quantity})` : ''}`}
                      />
                    )}
                    {sales.elevatedRiskSurcharge?.enabled && (
                      <ReadOnlyField
                        label='Elevated Risk'
                        value={`${formatCurrency(sales.elevatedRiskSurcharge.amount || 0)}${sales.elevatedRiskSurcharge.quantity > 0 ? ` (Qty: ${sales.elevatedRiskSurcharge.quantity})` : ''}`}
                      />
                    )}
                    {sales.ess?.enabled && (
                      <ReadOnlyField
                        label='ESS'
                        value={`${formatCurrency(sales.ess.amount || 0)}${sales.ess.quantity > 0 ? ` (Qty: ${sales.ess.quantity})` : ''}`}
                      />
                    )}
                    {sales.imitation?.enabled && (
                      <ReadOnlyField
                        label='Imitation'
                        value={`${formatCurrency(sales.imitation.amount || 0)}${sales.imitation.quantity > 0 ? ` (Qty: ${sales.imitation.quantity})` : ''}`}
                      />
                    )}
                    {sales.medicineHandlingCharges?.enabled && (
                      <ReadOnlyField
                        label='Medicine Handling'
                        value={`${formatCurrency(sales.medicineHandlingCharges.amount || 0)}${sales.medicineHandlingCharges.quantity > 0 ? ` (Qty: ${sales.medicineHandlingCharges.quantity})` : ''}`}
                      />
                    )}
                    {sales.nonStackablePallet?.enabled && (
                      <ReadOnlyField
                        label='Non Stackable Pallet'
                        value={`${formatCurrency(sales.nonStackablePallet.amount || 0)}${sales.nonStackablePallet.quantity > 0 ? ` (Qty: ${sales.nonStackablePallet.quantity})` : ''}`}
                      />
                    )}
                    {sales.remoteAreaCharges?.enabled && (
                      <ReadOnlyField
                        label='Remote Area'
                        value={`${formatCurrency(sales.remoteAreaCharges.amount || 0)}${sales.remoteAreaCharges.quantity > 0 ? ` (Qty: ${sales.remoteAreaCharges.quantity})` : ''}`}
                      />
                    )}
                    {sales.oversizeOverweightSurcharge?.enabled && (
                      <ReadOnlyField
                        label='Oversize/Overweight'
                        value={`${formatCurrency(sales.oversizeOverweightSurcharge.amount || 0)}${sales.oversizeOverweightSurcharge.quantity > 0 ? ` (Qty: ${sales.oversizeOverweightSurcharge.quantity})` : ''}`}
                      />
                    )}
                    {sales.restrictedDestinationSurcharge?.enabled && (
                      <ReadOnlyField
                        label='Restricted Destination'
                        value={`${formatCurrency(sales.restrictedDestinationSurcharge.amount || 0)}${sales.restrictedDestinationSurcharge.quantity > 0 ? ` (Qty: ${sales.restrictedDestinationSurcharge.quantity})` : ''}`}
                      />
                    )}
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-2 border-t pt-3'>
                  <ReadOnlyField
                    label='TOTAL OTHER CHARGES'
                    value={formatCurrency(sales.totalOtherCharges || 0)}
                  />
                  <ReadOnlyField
                    label='ADJUSTMENT'
                    value={formatCurrency(sales.adjustmentAmount || 0)}
                  />
                </div>

                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='FSC %'
                    value={`${sales.fscPercent || 0}%`}
                  />
                  <ReadOnlyField
                    label='FSC'
                    value={formatCurrency(sales.fsc || 0)}
                  />
                </div>

                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='DISCOUNT %'
                    value={`${sales.discountPercent || 0}%`}
                  />
                  <ReadOnlyField
                    label='DISCOUNT AMOUNT'
                    value={formatCurrency(sales.discountAmount || 0)}
                  />
                </div>

                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='TOTAL DISCOUNT'
                    value={formatCurrency(sales.totalDiscount || 0)}
                  />
                  <ReadOnlyField
                    label='FREIGHT AFTER DISCOUNT'
                    value={formatCurrency(sales.freightAfterDiscount || 0)}
                  />
                </div>

                {/* Totals */}
                <div className='border-t pt-3 mt-3 space-y-2'>
                  <ReadOnlyField
                    label='SUBTOTAL'
                    value={formatCurrency(sales.subtotal || 0)}
                  />
                  <ReadOnlyField
                    label='NON TAXABLE AMOUNT'
                    value={formatCurrency(sales.nonTaxableAmount || 0)}
                  />
                  <ReadOnlyField
                    label='TAXABLE AMOUNT'
                    value={formatCurrency(sales.taxableAmount || 0)}
                  />
                  <ReadOnlyField
                    label='GST %'
                    value={`${sales.gstPercent || 18}%`}
                  />
                  <div className='grid grid-cols-2 gap-2'>
                    <ReadOnlyField
                      label='CGST'
                      value={formatCurrency(sales.cgst || 0)}
                    />
                    <ReadOnlyField
                      label='SGST'
                      value={formatCurrency(sales.sgst || 0)}
                    />
                  </div>
                  <ReadOnlyField
                    label='GST AMOUNT'
                    value={formatCurrency(sales.gstAmount || 0)}
                  />
                  <ReadOnlyField
                    label='GRAND TOTAL'
                    value={formatCurrency(sales.grandTotal || 0)}
                    highlight
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Weight Summary & Invoice */}
          <div className='lg:col-span-3 p-4 space-y-4'>
            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-xs font-semibold text-gray-600 mb-3'>
                WEIGHT SUMMARY
              </h3>
              <div className='space-y-2'>
                <ReadOnlyField label='PCS' value={awbData.totalPieces || 0} />
                <ReadOnlyField
                  label='ACTUAL WEIGHT'
                  value={`${awbData.totalActualWeight || 0} KG`}
                />
                <ReadOnlyField
                  label='VOLUMETRIC WEIGHT'
                  value={`${awbData.totalVolumetricWeight || 0} KG`}
                />
                <ReadOnlyField
                  label='CHARGEABLE WEIGHT'
                  value={`${awbData.totalChargeableWeight || 0} KG`}
                  highlight
                />
              </div>
            </div>

            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-xs font-semibold text-gray-600 mb-3'>
                INVOICE DETAILS
              </h3>
              <div className='space-y-2'>
                <ReadOnlyField
                  label='INVOICE NUMBER'
                  value={sales.salesInvoiceNumber || '-'}
                />
                <ReadOnlyField
                  label='INVOICE DATE'
                  value={formatDate(sales.salesInvoiceDate)}
                />
              </div>
            </div>

            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-xs font-semibold text-gray-600 mb-3'>
                PAYMENT STATUS
              </h3>
              <div className='space-y-2'>
                <ReadOnlyField
                  label='PAID AMOUNT'
                  value={formatCurrency(sales.paidAmount || 0)}
                />
                <ReadOnlyField
                  label='BALANCE'
                  value={formatCurrency(
                    sales.balanceAmount || sales.balance || 0
                  )}
                  highlight
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: PURCHASE BILLING TAB
  // ============================================================================

  const renderPurchaseBillingTab = () => {
    const purchase = (awbData as any).purchaseBilling;
    if (!purchase) {
      return (
        <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 text-center'>
          <Truck className='h-12 w-12 text-gray-300 mx-auto mb-4' />
          <h3 className='text-lg font-medium text-gray-600 dark:text-gray-400'>
            No Purchase Billing Data
          </h3>
          <p className='text-sm text-gray-400 mt-2'>
            Purchase billing information will appear here once added.
          </p>
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate(`/awb/${awbNumber}/edit`)}
            className='mt-4 text-xs'
          >
            <Edit className='h-3 w-3 mr-1' />
            Add Purchase Billing
          </Button>
        </div>
      );
    }

    return (
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
        {/* Header */}
        <div className='flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
            DOCKET PURCHASE BILLING
          </h2>
          <div className='flex items-center gap-2'>
            <Badge className='bg-orange-100 text-orange-800 border border-orange-300 text-lg px-3 py-1'>
              {formatCurrency(purchase.grandTotal || purchase.finalAmount || 0)}
            </Badge>
            <Button
              variant='outline'
              size='sm'
              onClick={() => navigate(`/awb/${awbNumber}/edit`)}
              className='text-xs'
            >
              <Edit className='h-3 w-3 mr-1' />
              Edit
            </Button>
          </div>
        </div>

        {/* Main Content - 2 Column Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-0'>
          {/* LEFT COLUMN: Purchase Billing Details */}
          <div className='lg:col-span-5 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4'>
            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
                PURCHASE BILLING
              </h3>

              <ReadOnlyField
                label='COMPANY'
                value={getMasterName(masterNameLookup.companies, purchase.company)}
              />

              <div className='grid grid-cols-2 gap-3 mt-3'>
                <ReadOnlyField
                  label='CURRENCY'
                  value={getMasterName(
                    masterNameLookup.currencies,
                    purchase.currency
                  )}
                />
                <ReadOnlyField
                  label='GST TYPE'
                  value={purchase.gstType || 'IGST'}
                />
              </div>

              <div className='flex items-center gap-4 mt-3 mb-4 text-xs'>
                <span
                  className={`px-2 py-1 rounded ${purchase.gstApplicable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  GST {purchase.gstApplicable ? 'APPLICABLE' : 'NOT APPLICABLE'}
                </span>
              </div>

              <div className='space-y-2'>
                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='FREIGHT'
                    value={formatCurrency(purchase.freight || 0)}
                  />
                  <ReadOnlyField
                    label='FREIGHT PER KG'
                    value={formatCurrency(purchase.freightPerKg || 0)}
                  />
                </div>

                {/* Surcharges */}
                <div className='border-t pt-3 mt-3'>
                  <div className='text-xs font-semibold text-gray-600 mb-2'>
                    SURCHARGES
                  </div>
                  <div className='grid grid-cols-2 gap-2 text-xs'>
                    {purchase.addressCorrection?.enabled && (
                      <ReadOnlyField
                        label='Address Correction'
                        value={`${formatCurrency(purchase.addressCorrection.amount || 0)}${purchase.addressCorrection.quantity > 0 ? ` (Qty: ${purchase.addressCorrection.quantity})` : ''}`}
                      />
                    )}
                    {purchase.clearanceCharge?.enabled && (
                      <ReadOnlyField
                        label='Clearance Charge'
                        value={`${formatCurrency(purchase.clearanceCharge.amount || 0)}${purchase.clearanceCharge.quantity > 0 ? ` (Qty: ${purchase.clearanceCharge.quantity})` : ''}`}
                      />
                    )}
                    {purchase.elevatedRiskSurcharge?.enabled && (
                      <ReadOnlyField
                        label='Elevated Risk'
                        value={`${formatCurrency(purchase.elevatedRiskSurcharge.amount || 0)}${purchase.elevatedRiskSurcharge.quantity > 0 ? ` (Qty: ${purchase.elevatedRiskSurcharge.quantity})` : ''}`}
                      />
                    )}
                    {purchase.ess?.enabled && (
                      <ReadOnlyField
                        label='ESS'
                        value={`${formatCurrency(purchase.ess.amount || 0)}${purchase.ess.quantity > 0 ? ` (Qty: ${purchase.ess.quantity})` : ''}`}
                      />
                    )}
                    {purchase.imitation?.enabled && (
                      <ReadOnlyField
                        label='Imitation'
                        value={`${formatCurrency(purchase.imitation.amount || 0)}${purchase.imitation.quantity > 0 ? ` (Qty: ${purchase.imitation.quantity})` : ''}`}
                      />
                    )}
                    {purchase.medicineHandlingCharges?.enabled && (
                      <ReadOnlyField
                        label='Medicine Handling'
                        value={`${formatCurrency(purchase.medicineHandlingCharges.amount || 0)}${purchase.medicineHandlingCharges.quantity > 0 ? ` (Qty: ${purchase.medicineHandlingCharges.quantity})` : ''}`}
                      />
                    )}
                    {purchase.nonStackablePallet?.enabled && (
                      <ReadOnlyField
                        label='Non Stackable Pallet'
                        value={`${formatCurrency(purchase.nonStackablePallet.amount || 0)}${purchase.nonStackablePallet.quantity > 0 ? ` (Qty: ${purchase.nonStackablePallet.quantity})` : ''}`}
                      />
                    )}
                    {purchase.remoteAreaCharges?.enabled && (
                      <ReadOnlyField
                        label='Remote Area'
                        value={`${formatCurrency(purchase.remoteAreaCharges.amount || 0)}${purchase.remoteAreaCharges.quantity > 0 ? ` (Qty: ${purchase.remoteAreaCharges.quantity})` : ''}`}
                      />
                    )}
                    {purchase.oversizeOverweightSurcharge?.enabled && (
                      <ReadOnlyField
                        label='Oversize/Overweight'
                        value={`${formatCurrency(purchase.oversizeOverweightSurcharge.amount || 0)}${purchase.oversizeOverweightSurcharge.quantity > 0 ? ` (Qty: ${purchase.oversizeOverweightSurcharge.quantity})` : ''}`}
                      />
                    )}
                    {purchase.restrictedDestinationSurcharge?.enabled && (
                      <ReadOnlyField
                        label='Restricted Destination'
                        value={`${formatCurrency(purchase.restrictedDestinationSurcharge.amount || 0)}${purchase.restrictedDestinationSurcharge.quantity > 0 ? ` (Qty: ${purchase.restrictedDestinationSurcharge.quantity})` : ''}`}
                      />
                    )}
                  </div>
                </div>

                <div className='border-t pt-3'>
                  <ReadOnlyField
                    label='TOTAL OTHER CHARGES'
                    value={formatCurrency(purchase.totalOtherCharges || 0)}
                  />
                </div>

                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='FSC %'
                    value={`${purchase.fscPercent || 0}%`}
                  />
                  <ReadOnlyField
                    label='FSC'
                    value={formatCurrency(purchase.fsc || 0)}
                  />
                </div>

                <div className='grid grid-cols-2 gap-2'>
                  <ReadOnlyField
                    label='DISCOUNT %'
                    value={`${purchase.discountPercent || 0}%`}
                  />
                  <ReadOnlyField
                    label='DISCOUNT AMOUNT'
                    value={formatCurrency(purchase.discountAmount || 0)}
                  />
                </div>

                {/* Totals */}
                <div className='border-t pt-3 mt-3 space-y-2'>
                  <ReadOnlyField
                    label='SUBTOTAL'
                    value={formatCurrency(purchase.subtotal || 0)}
                  />
                  <ReadOnlyField
                    label='GST %'
                    value={`${purchase.gstPercent || 18}%`}
                  />
                  <div className='grid grid-cols-2 gap-2'>
                    <ReadOnlyField
                      label='CGST'
                      value={formatCurrency(purchase.cgst || 0)}
                    />
                    <ReadOnlyField
                      label='SGST'
                      value={formatCurrency(purchase.sgst || 0)}
                    />
                  </div>
                  <ReadOnlyField
                    label='GRAND TOTAL'
                    value={formatCurrency(purchase.grandTotal || 0)}
                    highlight
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Vendor Details */}
          <div className='lg:col-span-7 p-4 space-y-4'>
            {/* Vendor Details Section */}
            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
                VENDOR DETAILS
              </h3>

              <div className='grid grid-cols-2 gap-4'>
                <ReadOnlyField
                  label='PRODUCT'
                  value={getMasterName(masterNameLookup.products, awbData.product)}
                />
                <ReadOnlyField
                  label='SERVICE'
                  value={getMasterName(masterNameLookup.services, awbData.service)}
                />
                <ReadOnlyField
                  label='VENDOR'
                  value={getMasterName(masterNameLookup.vendors, awbData.vendor)}
                />
                <ReadOnlyField label='PCS' value={awbData.totalPieces || 0} />
              </div>

              {/* Weight Details */}
              <div className='mt-4 grid grid-cols-3 gap-4'>
                <ReadOnlyField
                  label='ACTUAL WEIGHT'
                  value={`${awbData.totalActualWeight || 0} KG`}
                />
                <ReadOnlyField
                  label='VOLUME WEIGHT'
                  value={`${(awbData.totalVolumetricWeight || 0).toFixed(2)} KG`}
                />
                <ReadOnlyField
                  label='CHARGEABLE WEIGHT'
                  value={`${awbData.totalChargeableWeight || 0} KG`}
                />
              </div>

              {/* Contract Details */}
              <div className='mt-4 grid grid-cols-2 gap-4'>
                <div>
                  <div className='text-xs font-semibold text-gray-600 mb-2'>
                    CFT CONTRACT
                  </div>
                  <div className='flex gap-2'>
                    <ReadOnlyField
                      label='CFT ID'
                      value={purchase.cftId || '-'}
                    />
                    <ReadOnlyField
                      label='CFT VALUE'
                      value={formatCurrency(purchase.cftValue || 0)}
                    />
                  </div>
                </div>
                <div>
                  <div className='text-xs font-semibold text-gray-600 mb-2'>
                    VENDOR CONTRACT
                  </div>
                  <div className='flex gap-2'>
                    <ReadOnlyField
                      label='CONTRACT ID'
                      value={purchase.vendorContractId || '-'}
                    />
                    <ReadOnlyField
                      label='TAT'
                      value={purchase.vendorTat || '-'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Invoice Section */}
            <div className='border border-gray-300 dark:border-gray-600 p-4'>
              <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
                VENDOR INVOICE
              </h3>

              <ReadOnlyField
                label='PAST VENDOR INVOICE'
                value={purchase.pastVendorInvoice || '-'}
              />

              <div className='grid grid-cols-2 gap-4 mt-3'>
                <ReadOnlyField
                  label='VENDOR INVOICE 1'
                  value={purchase.vendorInvoice1 || '-'}
                />
                <ReadOnlyField
                  label='INVOICE REMARKS 1'
                  value={purchase.invoiceRemarks1 || '-'}
                />
              </div>

              <div className='grid grid-cols-2 gap-4 mt-2'>
                <ReadOnlyField
                  label='VENDOR INVOICE 2'
                  value={purchase.vendorInvoice2 || '-'}
                />
                <ReadOnlyField
                  label='INVOICE REMARKS 2'
                  value={purchase.invoiceRemarks2 || '-'}
                />
              </div>
            </div>

            {/* Profit/Loss Section */}
            {(purchase.profitLoss !== undefined ||
              purchase.profitLossPercent !== undefined) && (
              <div className='border border-gray-300 dark:border-gray-600 p-4'>
                <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
                  PROFIT/LOSS
                </h3>
                <div className='grid grid-cols-2 gap-4'>
                  <ReadOnlyField
                    label='PROFIT/LOSS AMOUNT'
                    value={formatCurrency(purchase.profitLoss || 0)}
                    highlight
                  />
                  <ReadOnlyField
                    label='PROFIT/LOSS %'
                    value={`${(purchase.profitLossPercent || 0).toFixed(2)}%`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: TRACKING TAB
  // ============================================================================

  const renderTrackingTab = () => {
    // Filter out "AWB Updated" events for the main tracking view
    const events = (awbData.trackingEvents || []).filter(
      e => e.eventDescription !== 'AWB Updated'
    );
    const parcels = awbData.weightDimensions || [];

    return (
      <div className='space-y-4'>
        {/* Forwarding Numbers - Read Only Display */}
        {(awbData.forwardingNumber || awbData.forwardingNumber2) && (
          <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4'>
            <div className='flex flex-wrap gap-6'>
              {awbData.forwardingNumber && (
                <div>
                  <span className='text-xs font-medium text-gray-500 dark:text-gray-400'>
                    FORWARDING NUMBER
                  </span>
                  <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                    {awbData.forwardingNumber}
                  </p>
                </div>
              )}
              {awbData.forwardingNumber2 && (
                <div>
                  <span className='text-xs font-medium text-gray-500 dark:text-gray-400'>
                    FORWARDING NUMBER 2
                  </span>
                  <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                    {awbData.forwardingNumber2}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          {/* Left Column - Status & Summary */}
          <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
            <div className='flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
              <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                DELIVERY STATUS
              </h3>
              <Badge
                className={`${getStatusColor(awbData.currentStatus)} border`}
              >
                {awbData.currentStatus}
              </Badge>
            </div>

            <div className='p-4 space-y-4'>
              {/* Status Timeline */}
              <div className='relative'>
                {events.length === 0 ? (
                  <div className='text-center py-8'>
                    <Calendar className='h-12 w-12 text-gray-300 mx-auto mb-4' />
                    <h3 className='text-lg font-medium text-gray-600 dark:text-gray-400'>
                      No Tracking Events
                    </h3>
                    <p className='text-sm text-gray-400 mt-2'>
                      Tracking events will appear as the shipment progresses.
                    </p>
                  </div>
                ) : (
                  <div className='space-y-0'>
                    {(isTrackingExpanded ? events : events.slice(0, 5)).map(
                      (event: any, idx: number) => (
                        <div key={idx} className='flex gap-3 relative'>
                          {/* Timeline Line */}
                          {idx !==
                            (isTrackingExpanded ? events : events.slice(0, 5))
                              .length -
                              1 && (
                            <div className='absolute left-[7px] top-6 w-0.5 h-full bg-gray-200 dark:bg-gray-700' />
                          )}

                          {/* Timeline Dot */}
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mt-1 z-10 ${
                              idx === 0
                                ? 'bg-emerald-500 border-emerald-500'
                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                            }`}
                          />

                          {/* Content */}
                          <div className='flex-1 pb-4'>
                            <div className='flex items-start justify-between gap-2'>
                              <div>
                                <p
                                  className={`text-sm font-medium ${idx === 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-700 dark:text-gray-300'}`}
                                >
                                  {event.eventDescription}
                                </p>
                                <p className='text-xs text-gray-500 mt-0.5'>
                                  {event.eventLocation}
                                </p>
                              </div>
                              <span className='text-xs text-gray-400 whitespace-nowrap'>
                                {formatDate(event.eventDate)} {event.eventTime}
                              </span>
                            </div>
                            {event.remarks && (
                              <p className='text-xs text-gray-400 mt-1 italic'>
                                {event.remarks}
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    )}
                    {events.length > 5 && (
                      <button
                        type='button'
                        onClick={() =>
                          setIsTrackingExpanded(!isTrackingExpanded)
                        }
                        className='w-full text-xs text-blue-600 hover:text-blue-800 hover:underline text-center pt-2 focus:outline-none'
                      >
                        {isTrackingExpanded
                          ? 'Show less'
                          : `+${events.length - 5} more events`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Tracking Table */}
          <div className='space-y-4'>
            {/* AWB Tracking Table */}
            <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
              <div className='px-4 py-3 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800'>
                <h3 className='text-sm font-semibold text-blue-800 dark:text-blue-200'>
                  AWB TRACKING: {awbData.awbNumber}
                </h3>
              </div>

              <div className='overflow-x-auto'>
                <table className='w-full text-xs'>
                  <thead className='bg-gray-50 dark:bg-gray-700'>
                    <tr>
                      <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                        EVENT DATE TIME
                      </th>
                      <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                        EVENT DESCRIPTION
                      </th>
                      <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                        EVENT LOCATION
                      </th>
                      <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                        EVENT TYPE
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 dark:divide-gray-700'>
                    {events.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className='px-3 py-6 text-center text-gray-500'
                        >
                          No tracking events recorded
                        </td>
                      </tr>
                    ) : (
                      events.map((event: any, idx: number) => (
                        <tr
                          key={idx}
                          className={
                            idx === 0
                              ? 'bg-emerald-50 dark:bg-emerald-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }
                        >
                          <td className='px-3 py-2 whitespace-nowrap'>
                            <span className='font-medium'>
                              {formatDate(event.eventDate)}
                            </span>
                            <span className='text-gray-400 ml-1'>
                              {event.eventTime}
                            </span>
                          </td>
                          <td className='px-3 py-2'>
                            <span
                              className={
                                idx === 0
                                  ? 'font-semibold text-emerald-700 dark:text-emerald-400'
                                  : ''
                              }
                            >
                              {event.eventDescription}
                            </span>
                          </td>
                          <td className='px-3 py-2'>{event.eventLocation}</td>
                          <td className='px-3 py-2'>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                event.eventType === 'MANUAL'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                              }`}
                            >
                              {event.eventType}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Parcel Tracking Tables */}
            {parcels.map((parcel: any, parcelIdx: number) => (
              <div
                key={parcelIdx}
                className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
              >
                <div className='px-4 py-3 bg-orange-50 dark:bg-orange-900/30 border-b border-orange-200 dark:border-orange-800'>
                  <h3 className='text-sm font-semibold text-orange-800 dark:text-orange-200'>
                    PARCEL TRACKING:{' '}
                    {parcel.parcelNo || `${awbData.awbNumber}-${parcelIdx + 1}`}
                  </h3>
                </div>

                <div className='overflow-x-auto'>
                  <table className='w-full text-xs'>
                    <thead className='bg-gray-50 dark:bg-gray-700'>
                      <tr>
                        <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                          EVENT DATE TIME
                        </th>
                        <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                          EVENT DESCRIPTION
                        </th>
                        <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                          EVENT LOCATION
                        </th>
                        <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300'>
                          EVENT TYPE
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100 dark:divide-gray-700'>
                      {events.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className='px-3 py-4 text-center text-gray-500'
                          >
                            No tracking events
                          </td>
                        </tr>
                      ) : (
                        events.slice(0, 1).map((event: any, idx: number) => (
                          <tr
                            key={idx}
                            className='hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          >
                            <td className='px-3 py-2 whitespace-nowrap'>
                              <span className='font-medium'>
                                {formatDate(event.eventDate)}
                              </span>
                              <span className='text-gray-400 ml-1'>
                                {event.eventTime}
                              </span>
                            </td>
                            <td className='px-3 py-2'>
                              {event.eventDescription}
                            </td>
                            <td className='px-3 py-2'>{event.eventLocation}</td>
                            <td className='px-3 py-2'>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  event.eventType === 'MANUAL'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                                }`}
                              >
                                {event.eventType}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER: AUDIT LOGS TAB
  // ============================================================================

  // ============================================================================
  // RENDER: AUDIT LOGS TAB
  // ============================================================================

  const renderAuditLogsTab = () => {
    const logs = (awbData.trackingEvents || []).filter(
      e =>
        e.eventDescription === 'AWB Updated' ||
        e.eventDescription === 'AWB Created'
    );

    const toggleAuditLog = (idx: number) => {
      const newExpanded = new Set(expandedAuditLogs);
      if (newExpanded.has(idx)) {
        newExpanded.delete(idx);
      } else {
        newExpanded.add(idx);
      }
      setExpandedAuditLogs(newExpanded);
    };

    return (
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
        <div className='px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
            AUDIT LOGS
          </h2>
        </div>

        {logs.length === 0 ? (
          <div className='p-8 text-center'>
            <FileText className='h-12 w-12 text-gray-300 mx-auto mb-4' />
            <h3 className='text-lg font-medium text-gray-600 dark:text-gray-400'>
              No Audit Logs
            </h3>
            <p className='text-sm text-gray-400 mt-2'>
              Update history will appear here.
            </p>
          </div>
        ) : (
          <div className='p-4'>
            <div className='relative border-l-2 border-gray-200 dark:border-gray-700 ml-3 space-y-8'>
              {logs.map((log: any, idx: number) => (
                <div key={idx} className='relative pl-6'>
                  {/* Timeline Dot */}
                  <div
                    className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 ${
                      log.eventDescription === 'AWB Created'
                        ? 'bg-blue-500 border-blue-500'
                        : 'bg-white dark:bg-gray-800 border-gray-400 dark:border-gray-600'
                    }`}
                  />

                  {/* Content */}
                  <div className='flex flex-col gap-2'>
                    <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1'>
                      <div>
                        <p className='text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2'>
                          {log.eventDescription}
                          {log.changes && log.changes.length > 0 && (
                            <button
                              onClick={() => toggleAuditLog(idx)}
                              className='text-xs font-normal text-blue-600 hover:text-blue-800 hover:underline focus:outline-none'
                            >
                              {expandedAuditLogs.has(idx)
                                ? '(Hide Details)'
                                : `(View ${log.changes.length} Changes)`}
                            </button>
                          )}
                        </p>
                        <p className='text-xs text-gray-500 mt-1'>
                          Updated by:{' '}
                          <span className='font-medium text-gray-700 dark:text-gray-300'>
                            {log.createdBy?.name ||
                              log.createdBy ||
                              'Unknown User'}
                          </span>
                          {log.createdBy?.email && (
                            <span className='text-gray-400 ml-1'>
                              ({log.createdBy.email})
                            </span>
                          )}
                        </p>
                        {log.remarks && (
                          <p className='text-xs text-gray-600 dark:text-gray-400 mt-1 italic'>
                            "{log.remarks}"
                          </p>
                        )}
                      </div>
                      <div className='text-right'>
                        <p className='text-xs font-medium text-gray-900 dark:text-white'>
                          {formatDate(log.eventDate)}
                        </p>
                        <p className='text-xs text-gray-500'>{log.eventTime}</p>
                      </div>
                    </div>

                    {/* Changes Table */}
                    {expandedAuditLogs.has(idx) &&
                      log.changes &&
                      log.changes.length > 0 && (
                        <div className='mt-2 overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md'>
                          <table className='w-full text-xs'>
                            <thead className='bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700'>
                              <tr>
                                <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 w-1/3'>
                                  Field
                                </th>
                                <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 w-1/3'>
                                  Old Value
                                </th>
                                <th className='px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 w-1/3'>
                                  New Value
                                </th>
                              </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900'>
                              {log.changes.map((change: any, cIdx: number) => (
                                <tr key={cIdx}>
                                  <td className='px-3 py-2 font-mono text-gray-600 dark:text-gray-400'>
                                    {change.field}
                                  </td>
                                  <td className='px-3 py-2 text-red-600 dark:text-red-400 break-all'>
                                    {typeof change.oldValue === 'object'
                                      ? JSON.stringify(change.oldValue)
                                      : String(
                                          change.oldValue === undefined ||
                                            change.oldValue === null
                                            ? '-'
                                            : change.oldValue
                                        )}
                                  </td>
                                  <td className='px-3 py-2 text-green-600 dark:text-green-400 break-all'>
                                    {typeof change.newValue === 'object'
                                      ? JSON.stringify(change.newValue)
                                      : String(
                                          change.newValue === undefined ||
                                            change.newValue === null
                                            ? '-'
                                            : change.newValue
                                        )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // RENDER: EXTRA TAB
  // ============================================================================

  const renderExtraTab = () => {
    const attachments = awbData.attachments || [];
    const comments = awbData.comments || [];

    return (
      <div className='space-y-6'>
        {/* Attachments */}
        <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
          <div className='px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
            <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
              ATTACHMENTS
            </h2>
          </div>
          {attachments.length === 0 ? (
            <div className='p-8 text-center'>
              <FileText className='h-12 w-12 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-gray-600 dark:text-gray-400'>
                No Attachments
              </h3>
              <p className='text-sm text-gray-400 mt-2'>
                No files have been attached to this AWB.
              </p>
            </div>
          ) : (
            <div className='p-4 grid grid-cols-1 md:grid-cols-2 gap-3'>
              {attachments.map((att: any, idx: number) => (
                <div
                  key={idx}
                  className='flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border'
                >
                  <FileText className='h-8 w-8 text-blue-600' />
                  <div className='flex-1 min-w-0'>
                    <div className='text-sm font-medium truncate'>
                      {att.fileName}
                    </div>
                    <div className='text-xs text-gray-400'>{att.category}</div>
                  </div>
                  <Button size='sm' variant='outline'>
                    <Download className='h-4 w-4' />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments */}
        <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
          <div className='px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
            <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
              COMMENTS
            </h2>
          </div>
          {comments.length === 0 ? (
            <div className='p-8 text-center'>
              <Mail className='h-12 w-12 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-gray-600 dark:text-gray-400'>
                No Comments
              </h3>
              <p className='text-sm text-gray-400 mt-2'>
                No comments have been added to this AWB.
              </p>
            </div>
          ) : (
            <div className='p-4 space-y-3'>
              {comments.map((comment: any, idx: number) => (
                <div
                  key={idx}
                  className='p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border'
                >
                  <div className='text-sm'>{comment.comment}</div>
                  <div className='text-xs text-gray-400 mt-2'>
                    {formatDate(comment.createdAt)} • {comment.createdBy}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className='min-h-screen bg-gray-100 dark:bg-gray-900'>
      {/* Header */}
      <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
          <div className='flex items-center gap-3'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => navigate('/awb')}
              className='p-1 h-8 w-8'
            >
              <ChevronLeft className='h-5 w-5' />
            </Button>
            <div>
              <h1 className='text-lg font-bold text-gray-800 dark:text-gray-200'>
                AWB {awbData.awbNumber}
              </h1>
              <p className='text-xs text-gray-500'>View AWB Details</p>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={loadAWBData}
              className='text-xs'
            >
              <RefreshCw className='h-3 w-3 mr-1' />
              Refresh
            </Button>
            <Button
              size='sm'
              onClick={() => navigate(`/awb/${awbNumber}/edit`)}
              className='text-xs bg-blue-600 hover:bg-blue-700'
            >
              <Edit className='h-3 w-3 mr-1' />
              Edit AWB
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='p-4'>
        {renderTabs()}
        <div className='mt-0'>
          {activeTab === 'awb' && renderAWBDetailsTab()}
          {activeTab === 'sales' && renderSalesBillingTab()}
          {activeTab === 'purchase' && renderPurchaseBillingTab()}
          {activeTab === 'tracking' && renderTrackingTab()}
          {activeTab === 'kyc' && (
            <AWBKYCTab
              customerDocuments={
                typeof awbData.customer === 'object' &&
                awbData.customer?.kycDocuments
                  ? awbData.customer.kycDocuments
                  : []
              }
              shipperDocuments={awbData.shipper?.kycDocuments || []}
              onShipperDocsChange={() => {}}
              consigneeDocuments={awbData.consignee?.kycDocuments || []}
              onConsigneeDocsChange={() => {}}
              onSave={() => {}}
              isSaving={false}
              readOnly={true}
            />
          )}
          {activeTab === 'documents' && (
            <AWBDocumentsTab
              awbData={awbData}
              countries={masterData.countries}
            />
          )}
          {activeTab === 'audit' && renderAuditLogsTab()}
          {/* {activeTab === 'extra' && renderExtraTab()} */}
        </div>
      </div>
    </div>
  );
};

export default AWBViewPageNew;
